import { useEffect, useId, useRef, useState } from 'react';

import {
    ARROW_DOWN_KEY,
    ARROW_UP_KEY,
    ENTER_KEY,
    ESCAPE_KEY,
    FIRST_OPTION_INDEX,
    NO_FOCUSED_OPTION_INDEX,
    OPTION_INDEX_STEP,
    SPACE_KEY
} from './constants';
import type { UseSelectMenu } from './types';

// The behaviour every select menu shares, whatever it looks like: opening and closing,
// closing on an outside click, and choosing with the arrow keys, Enter, Space and Escape.
const useSelectMenu: UseSelectMenu = ({ disabled = false, onChange, options, value }) => {
    const reactId = useId();
    const fieldId = `select-menu-${reactId}`;
    const listboxId = `select-menu-list-${reactId}`;
    const containerRef = useRef<HTMLDivElement>(null);

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [focusedIndex, setFocusedIndex] = useState<number>(NO_FOCUSED_OPTION_INDEX);

    const selectedOption = options.find(option => option.value === value);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const selectedIndex = options.findIndex(option => option.value === value);

        setFocusedIndex(selectedIndex >= FIRST_OPTION_INDEX ? selectedIndex : FIRST_OPTION_INDEX);
    }, [isOpen, options, value]);

    const handleToggle = () => {
        if (disabled) {
            return;
        }

        setIsOpen(previous => !previous);
    };

    const handleOptionClick = (optionValue: string) => {
        onChange(optionValue);
        setIsOpen(false);
    };

    const lastOptionIndex = options.length - OPTION_INDEX_STEP;

    const handleKeyDown: ReturnType<UseSelectMenu>['handleKeyDown'] = event => {
        if (disabled) {
            return;
        }

        if (event.key === ESCAPE_KEY) {
            if (isOpen) {
                event.preventDefault();
                setIsOpen(false);
            }

            return;
        }

        if (event.key === ARROW_DOWN_KEY) {
            event.preventDefault();

            if (!isOpen) {
                setIsOpen(true);

                return;
            }

            setFocusedIndex(previous =>
                previous < lastOptionIndex ? previous + OPTION_INDEX_STEP : FIRST_OPTION_INDEX
            );

            return;
        }

        if (event.key === ARROW_UP_KEY) {
            event.preventDefault();

            if (!isOpen) {
                setIsOpen(true);

                return;
            }

            setFocusedIndex(previous =>
                previous > FIRST_OPTION_INDEX ? previous - OPTION_INDEX_STEP : lastOptionIndex
            );

            return;
        }

        if (event.key === ENTER_KEY || event.key === SPACE_KEY) {
            if (!isOpen) {
                event.preventDefault();
                setIsOpen(true);

                return;
            }

            if (focusedIndex >= FIRST_OPTION_INDEX && focusedIndex < options.length) {
                event.preventDefault();
                handleOptionClick(options[focusedIndex].value);
            }
        }
    };

    return {
        containerRef,
        fieldId,
        focusedIndex,
        handleKeyDown,
        handleOptionClick,
        handleToggle,
        isOpen,
        listboxId,
        selectedOption,
        setFocusedIndex
    };
};

export default useSelectMenu;
